import { Component, computed, output, signal, viewChild } from "@angular/core";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideTrash2 } from "@ng-icons/lucide";
import {
  BrnAlertDialogContent,
  HlmAlertDialog,
  HlmAlertDialogAction,
  HlmAlertDialogCancel,
  HlmAlertDialogContent,
  HlmButtonImports,
  HlmIconImports,
} from "@o2-fe/ui";
import { CustomTemplateType } from "../../../../shared/interfaces/custom-template.interface";

/**
 * Confirmation dialog shown before removing a custom document template.
 * Follows the standard O2 delete-confirmation pattern (centered icon,
 * title, description, destructive action + cancel).
 */
@Component({
  selector: "app-template-remove-confirm-dialog",
  standalone: true,
  imports: [
    NgIcon,
    HlmIconImports,
    HlmButtonImports,
    HlmAlertDialog,
    HlmAlertDialogContent,
    BrnAlertDialogContent,
    HlmAlertDialogAction,
    HlmAlertDialogCancel,
  ],
  providers: [provideIcons({ lucideTrash2 })],
  templateUrl: "./template-remove-confirm-dialog.component.html",
})
export class TemplateRemoveConfirmDialogComponent {
  /** ViewChild for the underlying Spartan alert dialog. */
  protected readonly dialogRef =
    viewChild.required<HlmAlertDialog>(HlmAlertDialog);

  /** Template type being removed — drives the Italian label in the dialog. */
  protected readonly templateType = signal<CustomTemplateType | null>(null);

  /** Italian label for the template type. */
  protected readonly templateTypeLabel = computed(() => {
    const type = this.templateType();
    if (type === "quote") return "preventivo";
    if (type === "contract") return "contratto";
    return "";
  });

  /** Emitted when the user confirms the removal. */
  readonly confirmed = output<void>();

  /**
   * Opens the dialog for a given template type.
   * Called by the parent component when the trash icon is clicked.
   */
  open(templateType: CustomTemplateType): void {
    this.templateType.set(templateType);
    this.dialogRef().open();
  }

  protected onConfirm(): void {
    this.dialogRef().close();
    this.confirmed.emit();
  }

  protected onCancel(): void {
    this.dialogRef().close();
  }
}
